import { useEffect, useSyncExternalStore } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import { ArrowLeft, Eye } from 'lucide-react'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { EmptyState } from '@/components/ui/empty-state'
import { PortalDashboardBody } from '@/components/member-portal/PortalDashboardBody'
import { DashboardAudienceProvider } from '@/components/member-portal/dashboardAudience'
import type { MemberPortalOrganization } from '@/components/member-portal/MemberSignIn'
import {
  portalDashboardKeys,
  type PortalDashboardKey,
  type PortalDashboardResponse,
} from '@/components/member-portal/portalDashboard.types'
import { getJSON } from '@/lib/api'
import { pushLocation } from './timelineFilters'

export interface OrganizationDashboardPreviewResponse extends PortalDashboardResponse {
  organization: MemberPortalOrganization
  enabled: boolean
  hidden_tiles: PortalDashboardKey[]
}
const labels: Record<PortalDashboardKey, string> = {
  spend: 'Spend',
  event_registrations: 'Event registrations',
  courses_completed: 'Courses completed',
  roster: 'Roster filled',
  personal_memberships: 'Personal memberships',
  groups: 'Groups',
}
export function OrganizationDashboardPreview({
  contactId,
  viewer,
}: {
  contactId: number
  viewer: string
}) {
  const client = useQueryClient()
  const session = useSyncExternalStore(
    (notify) =>
      client.getQueryCache().subscribe((event) => {
        if (event.query.queryKey.length === 1 && event.query.queryKey[0] === 'seat') notify()
      }),
    () =>
      JSON.stringify([
        client.getQueryData(['seat']),
        client.getQueryState(['seat'])?.dataUpdatedAt,
      ]),
  )
  const scope = JSON.stringify([viewer, session])
  return <Preview key={scope} contactId={contactId} viewer={scope} />
}
function Preview({ contactId, viewer }: { contactId: number; viewer: string }) {
  const base = `/api/v1/contacts/${contactId}/organization_dashboard_preview`
  const query = useQuery({
    queryKey: ['organization-dashboard-preview', viewer, contactId],
    queryFn: () => getJSON<OrganizationDashboardPreviewResponse>(base),
    retry: false,
  })
  if (query.isPending) return <EmptyState title="Loading preview" variant="quiet" />
  if (query.isError) return <Alert variant="destructive">Preview unavailable</Alert>
  return (
    <PreviewContent key={`${viewer}:${contactId}`} base={base} viewer={viewer} data={query.data} />
  )
}
function PreviewContent({
  base,
  viewer,
  data,
}: {
  base: string
  viewer: string
  data: OrganizationDashboardPreviewResponse
}) {
  const [params, setParams] = useSearchParams()
  const hidden = portalDashboardKeys.filter((key) => data.hidden_tiles.includes(key))
  const tiles = data.dashboard.tiles.filter(
    (tile) => portalDashboardKeys.includes(tile.key) && !hidden.includes(tile.key),
  )
  const available = portalDashboardKeys.filter(
    (key) => key !== 'roster' && tiles.some((tile) => tile.key === key),
  )
  const selected = available.find((key) => key === params.get('tile')) ?? available[0]
  useEffect(() => {
    if ((params.get('tile') ?? undefined) === selected) return
    const next = new URLSearchParams(params)
    if (selected) next.set('tile', selected)
    else next.delete('tile')
    setParams(next, { replace: true })
  }, [params, selected, setParams])
  const selectTile = (tile: PortalDashboardKey) => {
    if (!available.includes(tile)) return
    const next = new URLSearchParams({ tile })
    setParams(next)
  }
  const back = `/contacts/organizations/${data.organization.id}?tab=engagement`
  return (
    <div className="mx-auto w-full max-w-5xl space-y-4 px-4 py-6 sm:px-8 sm:py-8">
      <Button asChild variant="ghost" size="sm" className="-ml-3 gap-1 text-muted-foreground">
        <a
          href={back}
          onClick={(event) => {
            if (event.button || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
              return
            event.preventDefault()
            pushLocation(new URL(back, window.location.origin))
          }}
        >
          <ArrowLeft aria-hidden="true" className="size-4" />
          {data.organization.name}
        </a>
      </Button>
      <div className="rounded-md bg-accent/40 px-4 py-3">
        <div className="flex items-center gap-2">
          <Eye aria-hidden="true" className="size-4 shrink-0 text-primary" />
          <p className="text-body-sm font-medium text-foreground">
            Preview of what {data.organization.name} sees
          </p>
        </div>
        {!data.enabled && (
          <p className="mt-1 pl-6 text-caption text-muted-foreground">
            Organization dashboard is off. Organizations do not see this yet.
          </p>
        )}
      </div>
      {hidden.length > 0 && (
        <p className="text-caption text-muted-foreground">
          Hidden for your role: {hidden.map((key) => labels[key]).join(', ')}
        </p>
      )}
      <DashboardAudienceProvider base={base}>
        <PortalDashboardBody
          dashboard={{ ...data.dashboard, tiles }}
          organization={data.organization}
          viewer={viewer}
          selection={{ selected, canManage: false, tab: 'dashboard', selectTile }}
        />
      </DashboardAudienceProvider>
    </div>
  )
}
